import { useTranslation } from 'react-i18next'
import { ArrowRight, Sparkles } from 'lucide-react'
import { motion } from 'framer-motion'

interface CTABannerProps {
  title?: string
  subtitle?: string
  ctaText?: string
  ctaLink?: string
}

export function CTABanner({
  title,
  subtitle,
  ctaText,
  ctaLink,
}: CTABannerProps) {
  const { t } = useTranslation('common')

  const finalTitle = title || t('ctaBanner.defaultTitle')
  const finalCtaText = ctaText || t('ctaBanner.defaultCta')
  const finalCtaLink = ctaLink || import.meta.env.VITE_ESHOP_URL || '/shop'

  return (
    <section className="relative overflow-hidden bg-neutral-900">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
            backgroundSize: '40px 40px',
          }}
        />
      </div>

      {/* Decorative Elements */}
      <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-brand/10 to-transparent" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-brand/20 rounded-full blur-3xl -translate-x-1/2 translate-y-1/2" />

      <div className="relative max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[600px] lg:min-h-[700px]">
          {/* Left Side - Product Image */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="relative flex items-center justify-center p-8 lg:p-16 order-2 lg:order-1"
          >
            <div className="relative group cursor-pointer">
              {/* Glow Effect */}
              <div className="absolute inset-0 bg-brand/30 rounded-full blur-3xl scale-75 group-hover:scale-100 transition-transform duration-700" />

              {/* Main Image Container */}
              <div className="relative w-72 h-72 md:w-96 md:h-96 lg:w-[450px] lg:h-[450px] transition-transform duration-300 group-hover:scale-105 group-hover:rotate-2">
                {/* Image */}
                <img
                  src="/images/products/panettone-classico.png"
                  alt="Panettone Bonifanti"
                  className="w-full h-full object-contain drop-shadow-2xl"
                />

                {/* Floating Badge */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.5, duration: 0.6 }}
                  className="absolute -top-4 -right-4 bg-brand text-neutral-900 px-4 py-2 rounded-full font-heading font-bold text-sm shadow-lg"
                >
                  <span className="flex items-center gap-1">
                    <Sparkles className="w-4 h-4" />
                    {t('ctaBanner.since1932')}
                  </span>
                </motion.div>
              </div>

              {/* Decorative Ring */}
              <div className="absolute inset-0 border-2 border-dashed border-white/20 rounded-full scale-110 animate-spin-slow" />
            </div>
          </motion.div>

          {/* Right Side - Content */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="relative flex flex-col justify-center px-8 py-16 lg:px-16 lg:py-24 order-1 lg:order-2"
          >
            {/* Small Label */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="mb-6"
            >
              <span className="inline-flex items-center gap-2 text-brand font-heading font-medium tracking-wider uppercase text-sm">
                <span className="w-8 h-px bg-brand" />
                {t('ctaBanner.handmade')}
              </span>
            </motion.div>

            {/* Main Title */}
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
              className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl text-white leading-tight mb-6"
            >
              <span className="block">{finalTitle}</span>
              <span className="block text-brand mt-2">{t('ctaBanner.artisanal')}</span>
            </motion.h2>

            {/* Subtitle */}
            {subtitle && (
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.6 }}
                className="text-lg md:text-xl text-neutral-400 mb-10 max-w-md"
              >
                {subtitle}
              </motion.p>
            )}

            {/* Features */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.7 }}
              className="flex flex-wrap gap-4 mb-10"
            >
              {[t('ctaBanner.sourdough'), t('ctaBanner.naturalIngredients'), t('ctaBanner.brcCertified')].map((item) => (
                <span
                  key={item}
                  className="px-4 py-2 bg-white/5 border border-white/10 rounded-full text-white/80 text-sm font-medium"
                >
                  {item}
                </span>
              ))}
            </motion.div>

            {/* CTA Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.8 }}
            >
              <motion.a
                href={finalCtaLink}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="group relative inline-flex items-center gap-3 px-8 py-5 bg-brand text-neutral-900 font-heading font-semibold text-lg rounded-full overflow-hidden transition-all duration-300 hover:bg-brand-dark hover:text-neutral-900 hover:shadow-2xl hover:shadow-brand/25"
                target={finalCtaLink.startsWith('http') ? '_blank' : undefined}
                rel={finalCtaLink.startsWith('http') ? 'noopener noreferrer' : undefined}
              >
                {/* Button Glow */}
                <div className="absolute inset-0 bg-gradient-to-r from-brand-dark to-brand opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                {/* Button Content */}
                <span className="relative z-10">{finalCtaText}</span>
                <motion.span
                  className="relative z-10"
                  animate={{ x: [0, 5, 0] }}
                  transition={{ repeat: Infinity, duration: 1.5, ease: 'easeInOut' }}
                >
                  <ArrowRight className="w-5 h-5" />
                </motion.span>
              </motion.a>
            </motion.div>

            {/* Trust Badge */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 1 }}
              className="mt-10 flex items-center gap-4 text-neutral-500 text-sm"
            >
              <div className="flex -space-x-2">
                {[1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className="w-8 h-8 rounded-full bg-neutral-700 border-2 border-neutral-900 flex items-center justify-center text-xs text-white font-medium"
                  >
                    ★
                  </div>
                ))}
              </div>
              <span>{t('ctaBanner.satisfiedCustomers')}</span>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
